import { useEffect, useState } from "react";
import {
  categoryFromApi,
  productFromApi,
  type CategoriesResponse,
  type ProductsResponse,
} from "../lib/catalog";
import type { Product, ProductCategory } from "../types/catalog";

export function useCatalogData() {
  const [products, setProducts] = useState<Product[]>([]);
  const [productCategories, setProductCategories] = useState<ProductCategory[]>([]);
  const [catalogStatus, setCatalogStatus] = useState("Loading catalogue.");

  useEffect(() => {
    let isMounted = true;

    async function loadCatalog() {
      try {
        const [categoriesResponse, productsResponse] = await Promise.all([
          fetch("/api/categories"),
          fetch("/api/products"),
        ]);

        if (!categoriesResponse.ok || !productsResponse.ok) {
          throw new Error("Catalogue API unavailable.");
        }

        const categoriesData = await categoriesResponse.json<CategoriesResponse>();
        const productsData = await productsResponse.json<ProductsResponse>();
        const nextCategories = categoriesData.categories.map(categoryFromApi);
        const nextProducts = productsData.products.map(productFromApi);

        if (!isMounted) {
          return;
        }

        setProductCategories(nextCategories);

        if (nextProducts.length > 0) {
          setProducts(nextProducts);
          setCatalogStatus("");
        } else if (nextCategories.length > 0) {
          setProducts([]);
          setCatalogStatus("No products yet. Add your first product.");
        } else {
          setProducts([]);
          setCatalogStatus("No catalogue items yet. Add your first category or product.");
        }
      } catch {
        if (isMounted) {
          setProducts([]);
          setProductCategories([]);
          setCatalogStatus("Catalogue API unavailable.");
        }
      }
    }

    void loadCatalog();

    return () => {
      isMounted = false;
    };
  }, []);

  return {
    catalogStatus,
    productCategories,
    products,
    setCatalogStatus,
    setProductCategories,
    setProducts,
  };
}
